import React from 'react'
import { Box, Typography, Grid2 as Grid } from '@mui/material'
import { IPatientVitalSignsResponse } from 'types/patient/types'
import HealthCard, { IHealthCardProps } from '../../../../components/HealthCard'
import HealthSectionSkeleton from './skeleton'
import { minutesToHoursAndMinutes } from 'utils/hours'
import { decimalToString } from 'utils/decimal'
import { useVitalSignToPaint } from 'utils/hda'

interface IPatientVitalSignsSectionProps {
  patientId: string
  state: IStateStdTypes
  patientVitalSigns?: IPatientVitalSignsResponse
}

const PatientVitalSignsSection: React.FC<IPatientVitalSignsSectionProps> = ({
  patientId,
  state,
  patientVitalSigns
}) => {
  const vitalSignToPaint = useVitalSignToPaint(patientVitalSigns)

  if (state === 'loading') {
    return <HealthSectionSkeleton />
  }

  const vsKeyList: Array<string> = patientVitalSigns ? vitalSignToPaint : []

  if (!patientVitalSigns) {
    // TODO: create default component
    return <div>Erro ao buscar dados</div>
  }

  const healthData: IHealthCardProps[] = [
    {
      icon: <span className='material-symbols-outlined'>ecg_heart</span>,
      title: 'Frequência Cardíaca',
      data: 'heart_rate',
      values: [
        {
          type: 'bpm',
          value: patientVitalSigns.heart_rate
        }
      ],
      timestamp: patientVitalSigns.heart_rate_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>hr_resting</span>,
      title: 'Frequência Cardíaca em Repouso',
      values: [
        {
          type: 'bpm',
          value: patientVitalSigns.heart_rate_rest
        }
      ],
      data: 'heart_rate_rest',
      timestamp: patientVitalSigns.heart_rate_rest_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>blood_pressure</span>,
      title: 'Pressão Arterial',
      values: [
        {
          type: 'mmHg',
          value: patientVitalSigns.blood_pressure_systolic
            ? `${patientVitalSigns.blood_pressure_systolic}/${patientVitalSigns.blood_pressure_diastolic}`
            : ''
        }
      ],
      data: 'blood_pressure_systolic',

      timestamp: patientVitalSigns.blood_pressure_systolic_timestamp
    },
    // {
    //   icon: <span className='material-symbols-outlined'>vital_signs</span>,
    //   title: 'VFC rMSSD',
    //   values: [
    //     {
    //       type: 'ms',
    //       value: patientVitalSigns.hrv_rmssd
    //     }
    //   ],
    //   data: 'hrv_rmssd',
    //   timestamp: patientVitalSigns.hrv_rmssd_timestamp
    // },
    {
      icon: <span className='material-symbols-outlined'>vital_signs</span>,
      title: 'VFC SDNN',
      values: [
        {
          type: 'ms',
          value: patientVitalSigns.hrv_sdnn
        }
      ],
      data: 'hrv_sdnn',
      timestamp: patientVitalSigns.hrv_sdnn_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>spo2</span>,
      title: 'Saturação de O2',
      values: [
        {
          type: '%',
          value: patientVitalSigns.o2_saturation
        }
      ],
      data: 'o2_saturation',
      timestamp: patientVitalSigns.o2_saturation_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>pulmonology</span>,
      title: 'Frequência Respiratória',
      values: [
        {
          type: 'rpm',
          value: patientVitalSigns.respiratory_rate
        }
      ],
      data: 'respiratory_rate',
      timestamp: patientVitalSigns.respiratory_rate_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>hotel</span>,
      title: 'Tempo de Sono',
      data: 'sleep_total',
      values: [
        {
          type: 'h',
          value:
            minutesToHoursAndMinutes(patientVitalSigns.sleep_total)?.hours ??
            null
        },
        {
          type: 'min',
          value:
            minutesToHoursAndMinutes(patientVitalSigns.sleep_total)?.minutes ??
            null
        }
      ],
      timestamp: patientVitalSigns.sleep_total_timestamp
    },
    {
      icon: (
        <span className='material-symbols-outlined'>device_thermostat</span>
      ),
      title: 'Temperatura Corporal',
      values: [
        {
          type: 'ºC',
          value: patientVitalSigns.temperature_celsius
            ? decimalToString(
                patientVitalSigns.temperature_celsius,
                undefined,
                1
              )
            : null
        }
      ],
      data: 'temperature_celsius',
      timestamp: patientVitalSigns.temperature_celsius_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>thermostat</span>,
      title: 'Temperatura da Pele',
      values: [
        {
          type: 'ºC',
          value: patientVitalSigns.skin_temperature_celsius
            ? decimalToString(
                patientVitalSigns.skin_temperature_celsius,
                undefined,
                1
              )
            : null
        }
      ],
      data: 'skin_temperature_celsius',
      timestamp: patientVitalSigns.skin_temperature_celsius_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>footprint</span>,
      title: 'Passos',
      data: 'steps',
      values: [
        {
          type: 'passos',
          value: patientVitalSigns.steps
            ? decimalToString(Number(patientVitalSigns.steps))
            : null
        }
      ],
      timestamp: patientVitalSigns.steps_timestamp
    }
    // {
    //   icon: <BloodtypeOutlined />,
    //   title: 'Nível de Glicose',

    //   values: [
    //     {
    //       type: 'mg/dL',
    //       value: patientVitalSigns.blood_glucose_level
    //     }
    //   ],
    //   data: 'blood_glucose_level',
    //   timestamp: patientVitalSigns.blood_glucose_level_timestamp
    // }
  ]

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h6' fontWeight='bold' gutterBottom>
        Sinais Vitais:
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Grid container spacing={2}>
            {healthData.map((data, index) => (
              <Grid
                key={index}
                size={{
                  xs: 6,
                  md: 4,
                  lg: 3
                }}
              >
                <HealthCard
                  patientId={patientId}
                  {...data}
                  style={
                    vsKeyList.includes(data.data)
                      ? {
                          backgroundColor: 'rgba(218, 204, 62, 0.3)'
                        }
                      : undefined
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PatientVitalSignsSection
